/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 **/

import {
    getRequest,
    postRequest,
    putRequest,
    deleteRequest,
    createAction,
    stopLoading,
    startLoading,
    authErrorHandler,
    showMessage
} from "openstack-uicore-foundation/lib/utils/actions";
import {LISTS_CREATED} from "./presentation-actions";
import {getAccessTokenSafely} from "../utils/methods";

export const REQUEST_TRACK_CHAIRS           = 'REQUEST_TRACK_CHAIRS';
export const RECEIVE_TRACK_CHAIRS           = 'RECEIVE_TRACK_CHAIRS';
export const REQUEST_LISTS                  = 'REQUEST_LISTS';
export const RECEIVE_LISTS                  = 'RECEIVE_LISTS';
export const REMOVE_PRESENTATION            = 'REMOVE_PRESENTATION';
export const REORGANISE_SELECTIONS          = 'REORGANISE_SELECTIONS';
export const SAVING_LIST                    = 'SAVING_LIST';
export const LIST_SAVED                     = 'LIST_SAVED';
export const REQUEST_TEAM_LIST              = 'REQUEST_TEAM_LIST';
export const RECEIVE_TEAM_LIST              = 'RECEIVE_TEAM_LIST';
export const TEAM_LIST_CREATED              = 'TEAM_LIST_CREATED';


// no .catch() here on purpose: selections-page.js chains .then() off this call to decide
// whether to fetch the team list next, so the rejection needs to reach that call site
export const getTrackChairs = (trackSlug) => async (dispatch, getState) => {
    const { baseState } = getState();
    const { summit, allTracks } = baseState;

    if (!summit) return;

    const accessToken = await getAccessTokenSafely();
    const track = allTracks.find(c => c.slug === trackSlug);

    if (!track) return;

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
        expand: 'member, categories',
        per_page: 50,
        'filter[]': `track_id==${track.id}`
    };

    return getRequest(
        createAction(REQUEST_TRACK_CHAIRS),
        createAction(RECEIVE_TRACK_CHAIRS),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/track-chairs`,
        authErrorHandler,
        {track}
    )(params)(dispatch).finally(() => {
        dispatch(stopLoading());
    });
};

export const getSelectionLists = (trackId, memberId) => async (dispatch, getState) => {
    const { baseState, selectionsState } = getState();
    const { summit, selectionPlan } = baseState;

    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();
    const { trackChairs } = selectionsState;
    const trackChair = trackChairs.find(tc => `${tc.member.id}` === memberId);

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
        expand: 'interested_presentations, interested_presentations.presentation, selected_presentations, selected_presentations.presentation'
    };

    return getRequest(
        createAction(REQUEST_LISTS),
        createAction(RECEIVE_LISTS),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${trackId}/selection-lists/individual/owner/${memberId}`,
        authErrorHandler,
        {trackChair}
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};

export const removePresentation = (listType, presentationId) => async (dispatch, getState) => {
    const { baseState, loggedUserState, selectionsState } = getState();
    const { summit, selectionPlan } = baseState;

    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();
    const {track} = selectionsState;

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
    };

    return deleteRequest(
        null,
        createAction(REMOVE_PRESENTATION)({listType, presentationId}),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${track.id}/selection-lists/individual/presentation-selections/${listType}/presentations/${presentationId}`,
        authErrorHandler,
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};

export const reorderSelections = (listId, type, selections) => (dispatch) => {
    dispatch(createAction(REORGANISE_SELECTIONS)({listId, type, selections}));
};

export const saveLists = () => (dispatch, getState) => {
    const { selectionsState } = getState();
    const {individualList, teamList} = selectionsState;

    const presentationIdsOf = (selections) => selections.filter(s => s.presentation).map(s => s.presentation.id);

    if (individualList.isSelectedDirty) {
        const presentationIds = presentationIdsOf(individualList.selected_presentations);
        saveList(individualList.id, 'selected', presentationIds)(dispatch, getState)
    }

    if (individualList.isInterestedDirty) {
        const presentationIds = presentationIdsOf(individualList.interested_presentations);
        saveList(individualList.id, 'maybe', presentationIds)(dispatch, getState)
    }

    if (teamList.isDirty) {
        const presentationIds = presentationIdsOf(teamList.selected_presentations);
        saveList(teamList.id, 'selected', presentationIds, teamList.hash)(dispatch, getState)
    }
};

// the reorder PUT is hash-guarded server-side: a 412 means someone else saved this list
// since we last loaded it, so the generic authErrorHandler message isn't specific enough
const saveListErrorHandler = (err, res) => (dispatch) => {
    if (err.status === 412) {
        dispatch(showMessage({
            title: 'Conflict',
            html: 'This list was already modified by another track chair. Please reload the page to see the latest changes.',
            type: 'warning'
        }));
        return;
    }
    dispatch(authErrorHandler(err, res));
};

export const saveList = (listId, collection, presentations, hash = '') => async (dispatch, getState) => {
    const { baseState, selectionsState } = getState();
    const { summit, selectionPlan } = baseState;
    const { track } = selectionsState;

    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();

    const params = {
        access_token : accessToken,
    };

    dispatch(startLoading());

    putRequest(
        createAction(SAVING_LIST)({listId, collection}),
        createAction(LIST_SAVED),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${track.id}/selection-lists/${listId}/reorder`,
        {hash: hash, collection, presentations},
        saveListErrorHandler
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};

export const createLists = () => async (dispatch, getState) => {
    const { baseState, presentationsState } = getState();
    const { summit, selectionPlan } = baseState;
    const { track } = presentationsState;

    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
    };

    postRequest(
        null,
        createAction(LISTS_CREATED),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${track.id}/selection-lists/individual/owner/me`,
        {},
        authErrorHandler
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};

export const getTeamList = (trackId) => async (dispatch, getState) => {
    const { baseState } = getState();
    const { summit, selectionPlan } = baseState;

    // summit/selectionPlan can be reset to null mid-flight by a concurrent REQUEST_SUMMIT or
    // REQUEST_SELECTION_PLAN dispatched from a sibling layout (same race class as PR #63)
    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
        expand: 'selected_presentations, selected_presentations.presentation'
    };

    getRequest(
        createAction(REQUEST_TEAM_LIST),
        createAction(RECEIVE_TEAM_LIST),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${trackId}/selection-lists/team`,
        authErrorHandler
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};

export const createTeamList = () => async (dispatch, getState) => {
    const { baseState, presentationsState } = getState();
    const { summit, selectionPlan } = baseState;
    const { track } = presentationsState;

    if (!summit || !selectionPlan) return;

    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
        access_token : accessToken,
    };

    postRequest(
        null,
        createAction(TEAM_LIST_CREATED),
        `${window.API_BASE_URL}/api/v1/summits/${summit.id}/selection-plans/${selectionPlan.id}/tracks/${track.id}/selection-lists/team`,
        {},
        authErrorHandler
    )(params)(dispatch).catch(() => {}).finally(() => {
        dispatch(stopLoading());
    });
};